import {
  InventoryItem,
  InventoryItemCategory,
  InventoryItemUnit,
  InvoiceModel,
  InvoicesConfigModel,
  InvoiceStatus,
  InvoiceType,
} from "@jugl-web/rest-api";
import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { createContext, MutableRefObject } from "react";
import {
  FieldErrors,
  UseFieldArrayReturn,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";

export type InvoiceItemFormFields = Pick<
  InvoiceModel,
  | "date"
  | "notes"
  | "currency"
  | "payment_type"
  | "status"
  | "customer_id"
  | "due_on"
  | "items"
> & {
  billFrom: string;
  billTo: string;
};

export type WithTemporaryFields<G extends object> = G & {
  name?: string;
  discount?: number;
  price: string;
  quantity?: number;
  category?: InventoryItemCategory;
};

export type InvoiceItemCustom = WithTemporaryFields<{
  id: string;
  name: string;
  description: string;
  unit?: InventoryItemUnit | null;
  isCustom: true;
  discount: number;
}>;

export interface TaxFields {
  item: InvoiceTaxRow[];
  invoice: InvoiceTaxRow[];
}

export const InvoicesItemFormPageContext = createContext<{
  formValues: InvoiceItemFormFields;
  setFieldValue: (
    field: keyof InvoiceItemFormFields,
    value: string | null
  ) => void;
  register: UseFormRegister<InvoiceItemFormFields>;
  errors: FieldErrors<InvoiceItemFormFields>;
  watch: UseFormWatch<InvoiceItemFormFields>;
  submitForm: () => Promise<InvoiceModel | null>;
  globalSettings: InvoicesConfigModel | undefined;
  currencySymbol: string;
  resetBillFromField: () => void;
  invoiceLevelDiscount: {
    type: string;
    amount: number | null;
  };
  setInvoiceLevelDiscount: (discount: {
    type: string;
    amount: number | null;
  }) => void;
  taxFields: TaxFields;
  selectedTaxes: {
    item: string[];
    invoice: string[];
  };
  onAddTaxField: (
    level: "item" | "invoice",
    field: InvoiceTaxRow & { selected?: boolean }
  ) => void;
  onSetDefaultTaxField: (
    level: "item" | "invoice",
    id: string,
    isDefault: boolean
  ) => void;
  onEditTaxField: (level: "item" | "invoice", field: InvoiceTaxRow) => void;
  onRemoveTaxField: (level: "item" | "invoice", id: string) => void;
  onSetAsSelected: (level: "item" | "invoice", id: string) => void;
  onRemoveFromSelected: (level: "item" | "invoice", id: string) => void;
  id?: string;
  type: InvoiceType;
  savedStatus: InvoiceStatus | undefined;
  invoiceItemData: Partial<InvoiceModel>;
  customErrors: {
    items?: string;
    quantity?: {
      [itemId: string]: string;
    };
  };
  setCustomErrors: (errors: {
    items?: string;
    quantity?: {
      [itemId: string]: string;
    };
  }) => void;
  skipNavigationAlert: MutableRefObject<boolean>;
  isSubmitting: boolean;
  itemsField: UseFieldArrayReturn<InvoiceItemFormFields, "items", "id">;
  addInventoryItem: (item: InventoryItem, qty: number, idx?: number) => void;
} | null>(null);
